import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../../contexts/user.context";
import { useSearchParams } from "react-router-dom";
import {
  Delete,
  Get,
  Post,
  Patch,
  handleError,
} from "../../../utils/fetch.util";
import NoData from "../../../components/noData/noData.component";
import LoadingData from "../../../components/loadingData/loadingData.component";
import ConfirmationModal from "../../../components/modals/confirmationModal/confirmationModal.component";
import AddSecurityPlanModal from "../../../components/modals/AddSecurityPlanModal/AddSecurityPlanModal.component";
import EditSecurityPlanModal from "../../../components/modals/EditSecurityPlanModal/EditSecurityPlanModal.component";
import { FiEdit, FiSearch } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import ReactPaginate from "react-paginate";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import Select from "react-select";
import { useCommanData } from "../../../contexts/commanData.context";
import { Link } from "react-router-dom";
import Image from "../../../components/Image/Image.component";
import { QrCodeIcon } from "../../../components/staffCard/staffCard.styles";
import QrcodeIcon from "../../../assets/icons/qrcode.svg";
import { createPortal } from "react-dom";

const SecurityPlan = () => {
  const { t } = useTranslation();
  const { getValidToken, permissions } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const [securityPlans, setSecurityPlans] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    count: 0,
    page: Number(searchParams.get("page") || 1),
    limit: Number(searchParams.get("itemsPerPage") || 30),
  });
  const [filters, setFilters] = useState<any>({
    search: searchParams.get("search") || "",
    site_id: searchParams.get("site_id") || undefined,
  });
  const [searchInput, setSearchInput] = useState(filters.search || "");

  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [toggleAddModal, setToggleAddModal] = useState<boolean>(false);
  const [editPlan, setEditPlan] = useState<any>(null);
  const [planToDelete, setPlanToDelete] = useState<number | null>(null);
  const [showQrCode, setShowQrCode] = useState("");

  const { sectors } = useCommanData();
  const sitesOptions = sectors.flatMap((sector: any) =>
    (sector.sites || []).map((site: any) => ({
      value: site.site_id,
      label: site.site_name,
    }))
  );

  const getSecurityPlans = async () => {
    setIsFetching(true);
    const query: string[] = [];
    query.push(`page=${pagination.page}`);
    query.push(`pageItemsCount=${pagination.limit}`);
    if (filters.search.length > 0) query.push(`search=${filters.search}`);
    if (filters.site_id) query.push(`site_id=${filters.site_id}`);
    const res = await Get(
      `security-post/get-all-posts?${query.join("&")}`,
      getValidToken()
    );
    if (res?.error || !res?.data) {
      handleError(res);
      setIsFetching(false);
      return;
    }
    setSecurityPlans(res.data);
    setPagination((p) => ({ ...p, count: res.count }));
    setIsFetching(false);
  };

  const handleAdd = async (data: any) => {
    setIsLoading(true);
    const res = await Post(`security-post/create-post`, data, getValidToken());
    if (res?.error) handleError(res);
    setToggleAddModal(false);
    getSecurityPlans();
    setIsLoading(false);
  };

  const handleEdit = async (id: number, data: any) => {
    setIsLoading(true);
    const res = await Patch(
      `security-post/update-post?security_post_id=${id}`,
      data,
      getValidToken()
    );
    if (res?.error) handleError(res);
    setEditPlan(null);
    getSecurityPlans();
    setIsLoading(false);
  };

  const handleDelete = async () => {
    if (!planToDelete) return;
    setIsLoading(true);
    const res = await Delete(
      `security-post/delete-post?security_post_id=${planToDelete}`,
      getValidToken()
    );
    if (res?.error) handleError(res);
    setPlanToDelete(null);
    getSecurityPlans();
    setIsLoading(false);
  };

  useEffect(() => {
    getSecurityPlans();
    // eslint-disable-next-line
  }, [pagination.page, pagination.limit, filters.search, filters.site_id]);

  if (isFetching) return <LoadingData />;

  return (
    <>
      <div className="container bg-white p-6 px-8">
        <h1 className="text-5xl mt-2 mb-6">{t("security_plans")}</h1>
        <div className="flex items-center gap-3 mb-5">
          {permissions?.SecurityPost?.create && (
            <button
              onClick={() => setToggleAddModal(true)}
              className="main_btn me-auto"
            >
              {t("add_security_plan")}
            </button>
          )}
          <div className="w-52">
            <Select
              options={sitesOptions}
              isClearable
              placeholder={t("select_site")}
              value={
                sitesOptions.find(
                  (opt) => opt.value === Number(filters.site_id)
                ) || null
              }
              onChange={(selectedOption) => {
                setFilters({
                  ...filters,
                  site_id: selectedOption ? selectedOption.value : undefined,
                });
                setPagination({ ...pagination, page: 1 });
              }}
            />
          </div>
          <div className="relative w-44">
            <input
              type="text"
              placeholder={t("search")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setFilters({ ...filters, search: searchInput });
                  setPagination({ ...pagination, page: 1 });
                }
              }}
              className="input w-full rounded-full p-3 pr-10"
            />
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
              }}
              onClick={() => {
                setFilters({ ...filters, search: searchInput });
                setPagination({ ...pagination, page: 1 });
              }}
              aria-label="Search"
              tabIndex={-1}
              type="button"
            >
              <FiSearch size={20} />
            </button>
          </div>
        </div>
        {securityPlans.length === 0 ? (
          <NoData
            message={t("no_security_plans_found") || "No Security Plans found"}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="table-fixed rounded-md border-collapse w-full ">
              <thead>
                <tr>
                  <th className="border-2 p-4 py-2">{t("name")}</th>
                  <th className="border-2 p-4 py-2">{t("description")}</th>
                  <th className="border-2 p-4 py-2">{t("min_staff")}</th>
                  <th className="border-2 p-4 py-2">{t("site")}</th>
                  <th className="border-2 p-4 py-2">{t("qrcode")}</th>
                  <th className="border-2 p-4 py-2">{t("attendance")}</th>
                  <th className="border-2 p-4 py-2">{t("tasks")}</th>
                  <th className="border-2 p-4 py-2">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {securityPlans.map((plan: any) => (
                  <tr key={plan.id}>
                    <td className="border-2 p-4 py-2">{plan.name}</td>
                    <td className="border-2 p-4 py-2">{plan.description}</td>
                    <td className="border-2 p-4 py-2">{plan.minStaff}</td>
                    <td className="border-2 p-4 py-2">
                      {plan.site?.site_name || plan.site_id}
                    </td>
                    <td className="border-2 p-4 py-2">
                      <QrCodeIcon
                        src={QrcodeIcon}
                        title="qrcode"
                        onClick={() => setShowQrCode(plan.qrcode_image_url)}
                      />

                      {showQrCode == plan.qrcode_image_url &&
                        createPortal(
                          <div className="fixed inset-0 flex items-center justify-center !z-[50]">
                            <div className="fixed inset-0 bg-black/50 z-[40]" onClick={() => {
                              setShowQrCode("");
                            }} />

                            <Image src={showQrCode} alt="Plan's QrCode" className="size-[300px] relative z-[50] rounded-lg" />
                          </div>,
                          document.body
                        )}
                    </td>
                    <td className="border-2 p-4 py-2">
                      <Link
                        to={`/security-plans-attendance?securityPostId=${plan?.id}&createdAt=${new Date().toISOString().split("T")[0]}`}
                        className="cursor-pointer text-primary"
                      >
                        {t("view")}
                      </Link>
                    </td>
                    <td className="border-2 p-4 py-2">
                      <Link
                        to={`/security-plans/${plan.id}`}
                        className="cursor-pointer text-primary"
                      >
                        {t("view")}
                      </Link>
                    </td>
                    <td className="border-2 p-4 py-2">
                      <div className="flex gap-2">
                        {permissions?.SecurityPost?.update && (
                          <button
                            onClick={() => setEditPlan(plan)}
                            className="icon_btn"
                          >
                            <FiEdit size={20} />
                          </button>
                        )}
                        {permissions?.SecurityPost?.delete && (
                          <button
                            onClick={() => setPlanToDelete(plan.id)}
                            className="icon_btn"
                          >
                            <RiDeleteBinLine size={20} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination.count > pagination.limit && (
          <ReactPaginate
            className="pagination_table"
            containerClassName={"pagination"}
            activeClassName={"active"}
            pageClassName={"page-item"}
            forcePage={pagination.page - 1}
            onPageChange={(event) =>
              setPagination({ ...pagination, page: event.selected + 1 })
            }
            breakLabel="..."
            pageCount={Math.ceil(pagination.count / pagination.limit)}
            previousLabel={
              pagination.page == 1 ? null : (
                <div
                  style={{
                    backgroundColor: "#B8C1CC",
                    width: "36px",
                    height: "36px",
                  }}
                  className="flex items-center justify-center rounded-full text-white"
                >
                  <GrFormPrevious size={28} />
                </div>
              )
            }
            nextLabel={
              pagination.page ==
              Math.ceil(pagination.count / pagination.limit) ? null : (
                <div
                  style={{
                    backgroundColor: "#B8C1CC",
                    width: "36px",
                    height: "36px",
                  }}
                  className="flex items-center justify-center rounded-full text-white"
                >
                  <GrFormNext size={28} />
                </div>
              )
            }
          />
        )}
      </div>
      <AddSecurityPlanModal
        isOpen={toggleAddModal || !!editPlan}
        onClose={() => {
          setToggleAddModal(false);
          setEditPlan(null);
        }}
        onSubmit={async (data: any) => {
          if (editPlan) {
            await handleEdit(editPlan.id, data);
            setEditPlan(null);
          } else {
            await handleAdd(data);
          }
          setToggleAddModal(false);
        }}
        isLoading={isLoading}
        initialData={editPlan}
      />
      <ConfirmationModal
        isShown={!!planToDelete || false}
        closeConfirmationModal={() => setPlanToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isLoading}
        message={t("are_you_sure_delete")}
        title={t("confirmation")}
      />
    </>
  );
};

export default SecurityPlan;
